import React, { useEffect, useRef, useCallback } from 'react';
import { logTelegramWidgetEvent } from '../../services/api';
import './TelegramLoginButton.css';

interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

interface TelegramLoginButtonProps {
  botName: string;
  onAuth: (user: TelegramUser) => void;
  buttonSize?: 'large' | 'medium' | 'small';
  cornerRadius?: number;
  requestAccess?: 'write' | undefined;
  showUserPhoto?: boolean;
  lang?: string;
}

declare global {
  interface Window {
    TelegramLoginWidget: {
      dataOnauth: (user: TelegramUser) => void;
    };
  }
}

const TelegramLoginButton: React.FC<TelegramLoginButtonProps> = ({
  botName,
  onAuth,
  buttonSize = 'large',
  cornerRadius = 8,
  requestAccess = 'write',
  showUserPhoto = true,
  lang = 'en'
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const onAuthRef = useRef(onAuth);

  // Keep onAuth ref updated
  useEffect(() => {
    onAuthRef.current = onAuth;
  }, [onAuth]);

  useEffect(() => {
    // Use a fixed, global callback name that Telegram expects
    const callbackName = 'onTelegramAuth';

    // Define the callback function on window
    (window as any)[callbackName] = (user: TelegramUser) => {
      console.log('Telegram auth callback received:', user);
      logTelegramWidgetEvent('callback_received', {
        telegram_id: user?.id,
        telegram_username: user?.username,
      });
      onAuthRef.current(user);
    };

    // Create the Telegram widget script
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', botName);
    script.setAttribute('data-size', buttonSize);
    script.setAttribute('data-radius', cornerRadius.toString());
    script.setAttribute('data-onauth', `${callbackName}(user)`);
    script.setAttribute('data-lang', lang);

    if (requestAccess) {
      script.setAttribute('data-request-access', requestAccess);
    }

    if (!showUserPhoto) {
      script.setAttribute('data-userpic', 'false');
    }

    script.async = true;

    // Add error handling
    script.onerror = () => {
      console.error('Failed to load Telegram widget script');
      logTelegramWidgetEvent('widget_script_error', {
        error_message: 'telegram-widget.js failed to load',
      });
    };

    // Clear container and add script
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
      containerRef.current.appendChild(script);
      logTelegramWidgetEvent('widget_mounted');
    }

    // Cleanup
    return () => {
      delete (window as any)[callbackName];
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [botName, buttonSize, cornerRadius, requestAccess, showUserPhoto, lang]);

  return (
    <div className="telegram-login-container">
      <div ref={containerRef} className="telegram-widget-container" />
    </div>
  );
};

export default TelegramLoginButton;
